import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  Divider,
} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import AppTheme from '../../styles/AppTheme';
import bg1 from '../../assets/bg1.jpg';
import { yellow } from '../../styles/ThemePrimitives';
import fmb52 from '../../assets/fmb52.png';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.fmb52.com/api';

const PageShell = styled(Stack)(({ theme }) => ({
  minHeight: '100svh',
  boxSizing: 'border-box',
  padding: theme.spacing(2),
  paddingLeft: `max(${theme.spacing(2)}, env(safe-area-inset-left))`,
  paddingRight: `max(${theme.spacing(2)}, env(safe-area-inset-right))`,
  paddingBottom: `max(${theme.spacing(2)}, env(safe-area-inset-bottom))`,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: `linear-gradient(180deg, rgba(255,248,240,0.72) 0%, rgba(255,255,255,0.55) 100%), url(${bg1})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
}));

const PayCard = styled(MuiCard)(({ theme }) => ({
  width: '100%',
  maxWidth: 440,
  margin: '0 auto',
  boxSizing: 'border-box',
  borderRadius: 16,
  border: `1px solid ${yellow[200] || '#E8D9B5'}`,
  backgroundColor: '#FFFaf3',
  boxShadow: '0 12px 40px rgba(60, 40, 10, 0.12)',
  overflow: 'hidden',
}));

const formatInr = (v) =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function InfoRow({ label, value, emphasize = false, muted = false }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
        py: 0.85,
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          color: muted ? 'text.secondary' : 'text.primary',
          fontWeight: 600,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: emphasize ? 16 : 14,
          fontWeight: emphasize ? 800 : 600,
          color: emphasize ? 'error.main' : 'text.primary',
          textAlign: 'right',
          wordBreak: 'break-word',
          flex: '1 1 auto',
          minWidth: 0,
          lineHeight: 1.35,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PayPublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState('');

  const fetchInfo = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/pay/${encodeURIComponent(token)}`, {
        headers: { Accept: 'application/json' },
      });
      const json = await resp.json();
      if (!resp.ok || !json?.success) {
        throw new Error(json?.message || 'Pay link not found');
      }
      setInfo(json.data);
      const due = Number(json.data?.due ?? 0);
      const previousDue = Number(json.data?.previous_due ?? 0);
      setAmount(String(due + previousDue));
    } catch (e) {
      setInfo(null);
      setError(e.message || 'Unable to load pay link');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    document.title = 'Hub Payment - FMB 52';
    fetchInfo();
  }, [fetchInfo]);

  const handlePay = async () => {
    if (!info || !token) return;
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Enter a valid amount');
      return;
    }
    const maxPayable = Number(info.due || 0) + Number(info.previous_due || 0);
    if (amt > maxPayable + 0.001) {
      setError(`Amount cannot exceed due (₹${formatInr(maxPayable)})`);
      return;
    }

    setPaying(true);
    setError(null);
    setSuccess(null);

    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error('Failed to load Razorpay Checkout');

      const orderResp = await fetch(`${API_BASE}/pay/${encodeURIComponent(token)}/order`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt }),
      });
      const orderJson = await orderResp.json();
      if (!orderResp.ok || !orderJson?.success) {
        throw new Error(orderJson?.message || 'Failed to create order');
      }

      const order = orderJson.data;
      const options = {
        key: order.razorpay_key_id || info.razorpay_key_id,
        amount: Math.round(Number(order.amount) * 100),
        currency: order.currency || 'INR',
        name: 'Faiz ul Mawaid il Burhaniyah - Kolkata',
        description: `Hub Payment ${info.year || ''}`.trim(),
        order_id: order.order_id,
        prefill: {
          name: order.name || info.name,
          contact: (order.mobile || info.mobile || '').replace(/^91/, ''),
        },
        handler: async (response) => {
          try {
            const verifyResp = await fetch(`${API_BASE}/pay/${encodeURIComponent(token)}/verify`, {
              method: 'POST',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            const verifyJson = await verifyResp.json();
            if (!verifyResp.ok || !verifyJson?.success) {
              const detail = [verifyJson?.message, verifyJson?.error, verifyJson?.receipt_message]
                .filter(Boolean)
                .filter((v, i, arr) => arr.indexOf(v) === i)
                .join(' — ');
              throw new Error(detail || 'Payment verification failed');
            }
            setSuccess(
              `Payment successful. Receipt created for ₹${formatInr(verifyJson?.data?.amount ?? amt)}. WhatsApp receipt will be sent shortly.`
            );
            await fetchInfo();
          } catch (err) {
            setError(err.message || 'Verification failed');
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setError(resp?.error?.description || 'Payment failed');
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      setError(e.message || 'Unable to start payment');
      setPaying(false);
    }
  };

  return (
    <AppTheme>
      <CssBaseline />
      <PageShell>
        <PayCard>
          {/* Brand header */}
          <Box
            sx={{
              px: { xs: 2.5, sm: 3 },
              pt: 3,
              pb: 2,
              textAlign: 'center',
              background: `linear-gradient(180deg, ${yellow[100] || '#F4EBD0'} 0%, #FFFaf3 100%)`,
            }}
          >
            <Box
              component="img"
              src={fmb52}
              alt="FMB 52"
              sx={{ width: 78, height: 78, objectFit: 'contain', mb: 1.25 }}
            />
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: 15, sm: 16 },
                lineHeight: 1.35,
                color: yellow[900] || '#4A3728',
                letterSpacing: 0.15,
              }}
            >
              Faiz ul Mawaid il Burhaniyah - Kolkata
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                fontWeight: 700,
                fontSize: 20,
                color: yellow[800] || '#5C4033',
              }}
            >
              Hub Payment
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && error && !info && (
              <Alert severity="error">{error}</Alert>
            )}

            {!loading && info && (
              <Stack spacing={2.25}>
                {/* Family */}
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #EDE4D4',
                    bgcolor: '#fff',
                    px: 1.75,
                    py: 0.5,
                  }}
                >
                  <InfoRow label="Name" value={info.name} />
                  <Divider />
                  <InfoRow label="ITS" value={info.its} />
                  <Divider />
                  <InfoRow label="Year" value={info.year} muted />
                </Box>

                {/* Amounts */}
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #EDE4D4',
                    bgcolor: '#fff',
                    px: 1.75,
                    py: 0.5,
                  }}
                >
                  <InfoRow label="Commitment" value={`₹${formatInr(info.commitment)}`} />
                  <Divider />
                  <InfoRow label="Paid" value={`₹${formatInr(info.paid)}`} />
                  <Divider />
                  <InfoRow
                    label="Due"
                    value={
                      Number(info.previous_due) > 0
                        ? `₹${formatInr(info.due)} [ Previous Due : Rs. ${Number(info.previous_due).toLocaleString('en-IN', { maximumFractionDigits: 0 })} ]`
                        : `₹${formatInr(info.due)}`
                    }
                    emphasize
                  />
                </Box>

                {Number(info.due) + Number(info.previous_due || 0) <= 0 ? (
                  <Alert severity="success">No outstanding due for this family.</Alert>
                ) : (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'text.secondary',
                        mb: 1,
                      }}
                    >
                      Enter amount to pay
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      label="Amount (₹)"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      inputProps={{ min: 1, step: '0.01', max: Number(info.due || 0) + Number(info.previous_due || 0) }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        mb: 1.5,
                        mt: 0.5,
                        bgcolor: '#fff',
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                      }}
                    />

                    {error && (
                      <Alert severity="error" sx={{ mb: 1.5 }}>
                        {error}
                      </Alert>
                    )}
                    {success && (
                      <Alert severity="success" sx={{ mb: 1.5 }}>
                        {success}
                      </Alert>
                    )}

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handlePay}
                      disabled={paying || !info.razorpay_key_id}
                      sx={{
                        py: 1.35,
                        borderRadius: 2,
                        fontWeight: 800,
                        fontSize: 16,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none' },
                      }}
                    >
                      {paying ? <CircularProgress size={22} color="inherit" /> : 'Pay Now'}
                    </Button>

                    {!info.razorpay_key_id && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                        Razorpay is not configured on the server.
                      </Alert>
                    )}

                    <Typography
                      sx={{
                        mt: 1.5,
                        fontSize: 11,
                        color: 'text.secondary',
                        textAlign: 'center',
                      }}
                    >
                      You can pay the full due or any amount up to ₹
                      {formatInr(Number(info.due || 0) + Number(info.previous_due || 0))}.
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        </PayCard>
      </PageShell>
    </AppTheme>
  );
}
